import React, { useState, useEffect, useRef, useMemo } from "react";
import Modal from 'react-modal';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/slices/store";
import { formatMetricName, rawDimensions, formatMetricString, sortByExchangeOrder } from "src/helpers/commonHelper";
import { setSelectedExchangeMetrics, selectAllExchangesHaveNoData, selectExchangeByName, setExchangeOrder } from "src/slices/Report/reducer";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { buildCacheKey, setTabSelectedMetrics } from "src/slices/TabData/reducer";
import { tabRegistry } from "./tabRegistry";
import axiosInstance from 'src/common/axiosInstance';
import { toast } from 'react-toastify';
import { useTabAccess } from "src/hooks/useTabAccess";

// Props interface for CustomizeMetricsModal component
interface CustomizeMetricsModalProps {
  modalIsOpen: boolean;
  closeModal: () => void;
  selectedExchangesData: any[];
}

// Interface for exchange metrics state
interface ExchangeMetricsState {
  id: number;
  name: string;
  iconUrl: string;
  display_metrics: string[];
  defaultMetrics: string[];
  selectedMetrics: string[];
}

// Sortable tab component for drag-and-drop reordering
const SortableTab = ({
  exchange,
  isActive,
  selectedCount,
  onClick,
}: {
  exchange: ExchangeMetricsState;
  isActive: boolean;
  selectedCount: number;
  onClick: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: exchange.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`tab ${isActive ? "tab custom-tab" : "tab"}`}
      onClick={onClick}
    >
      {exchange.name}
      <span className="selected-count">{selectedCount}</span>
    </div>
  );
};

// Main component for customizing metrics
const CustomizeMetricsModal: React.FC<CustomizeMetricsModalProps> = ({
  modalIsOpen,
  closeModal,
  selectedExchangesData
}) => {
  const [loading, setLoading] = useState(false);
  const [includeAISummary, setIncludeAISummary] = useState(false);
  const [allMetrics, setAllMetrics] = useState(false);
  const [selectedExchangeId, setSelectedExchangeId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [exchangeSelectionState, setExchangeSelectionState] = useState<ExchangeMetricsState[]>([]);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasSelectedMetrics, setHasSelectedMetrics] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  // Tab metrics local state — keyed by "exchangeId:tabId". Same pattern as exchangeSelectionState:
  // local during modal interaction, persisted to Redux on Save, passed directly on Export.
  const [tabMetricsState, setTabMetricsState] = useState<Record<string, string[]>>({});
  const dispatch = useDispatch();
  const filters = useSelector((state: RootState) => state.filters);
  const reportBy = useSelector((state: RootState) => state.filters.dataBy);
  const orderData = useSelector((state: RootState) => state.reports.selectedReportOrder);
  const exchangeOrder = useSelector((state: RootState) => state.reports.exchangeOrder);
  const visibleExchangeIds = useSelector((state: RootState) => state.reports.visibleExchangeIds);
  const allExchangesHaveNoData = useSelector((state: RootState) => {
    const mainEmpty = selectAllExchangesHaveNoData(state.reports);
    if (!mainEmpty) return false;
    // Main tabs have no data — check if any non-main tab has data
    return !selectedExchangesData.some(exchange =>
      tabRegistry.some(tab => {
        if (tab.tabId === "main") return false;
        const key = buildCacheKey(exchange.id, tab.tabId);
        const entry = state.tabData.entries[key];
        return entry && entry.rows.length > 0;
      })
    );
  });
  const tabDataEntries = useSelector((state: RootState) => state.tabData.entries);
  const authUser = useSelector((state: RootState) => state.login.user.user);
  const tabAccess = useTabAccess();

  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleTabDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentIds = exchangeSelectionState.map(ex => ex.id);
    const oldIndex = currentIds.indexOf(Number(active.id));
    const newIndex = currentIds.indexOf(Number(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(currentIds, oldIndex, newIndex);
    setExchangeSelectionState(prev => {
      const map = new Map(prev.map(ex => [ex.id, ex]));
      return newOrder.map((id: number) => map.get(id)!);
    });
    dispatch(setExchangeOrder(newOrder));
  };

  const checkForOverflow = () => {
    const container = tabContainerRef.current;
    if (!container) return;

    const hasOverflow = container.scrollWidth > container.clientWidth;
    setShowScrollButtons(hasOverflow);

    if (hasOverflow) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  };


  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    checkForOverflow();
    window.addEventListener("resize", checkForOverflow);
    container.addEventListener("scroll", checkForOverflow);

    return () => {
      window.removeEventListener("resize", checkForOverflow);
      container.removeEventListener("scroll", checkForOverflow);
    };
  }, [exchangeSelectionState]);



  // Initialize state when modal opens
  useEffect(() => {
    if (modalIsOpen && selectedExchangesData?.length > 0) {
      let initialState = selectedExchangesData.map(exchange => ({
        id: exchange.id,
        name: exchange.name,
        iconUrl: exchange.iconUrl,
        display_metrics: exchange.display_metrics || [],
        defaultMetrics: exchange.defaultMetrics || [],
        selectedMetrics: exchange.selectedMetrics || []
      }));

      // Sync tab order from exchange filter dropdown
      initialState = sortByExchangeOrder(initialState, exchangeOrder);

      setExchangeSelectionState(initialState);
      setSelectedExchangeId(initialState[0]?.id || null);
      setAllMetrics(false);
      setIncludeAISummary(false);
      setSearchTerm("");
      setIsInitialLoad(true);

      // Seed tab metrics local state from Redux (or defaults from registry)
      const tmState: Record<string, string[]> = {};
      selectedExchangesData.forEach(exchange => {
        tabRegistry.forEach(tab => {
          if (tab.tabId === "main" || !tab.availableMetrics) return;
          if (tab.tabId === "creative") return; // creative uses its own Customize Columns dropdown
          if (tab.exportOnly) return;
          if (tab.showForExchange && !tab.showForExchange(exchange)) return;
          if (authUser.user_type === 'Client') {
            if (tab.tabId in tabAccess) {
              if (Array.isArray(tabAccess[tab.tabId]) && tabAccess[tab.tabId].length === 0) return;
            } else {
              if (!tab.defaultSelectedMetrics) return;
            }
          }

          const stateKey = `${exchange.id}:${tab.tabId}`;
          const cacheKey = buildCacheKey(exchange.id, tab.tabId);
          const entry = tabDataEntries[cacheKey];
          // If tab was visited, use its selectedMetrics; otherwise default to permitted metrics
          const userPermitted = authUser.user_type === 'Client' && Array.isArray(tabAccess[tab.tabId])
            ? tabAccess[tab.tabId] as string[]
            : null;
          const allIds = userPermitted
            ? tab.availableMetrics.filter(m => userPermitted.includes(m.id)).map(m => m.id)
            : tab.availableMetrics.map(m => m.id);
          // Apply defaultSelectedMetrics only for Client users without explicit tab_access
          const defaultIds = authUser.user_type === 'Client' && !(tab.tabId in tabAccess) && tab.defaultSelectedMetrics
            ? tab.defaultSelectedMetrics.filter(m => allIds.includes(m))
            : allIds;
          tmState[stateKey] = entry
            ? [...entry.selectedMetrics]
            : defaultIds;
        });
      });
      setTabMetricsState(tmState);
    }
  }, [modalIsOpen, selectedExchangesData]);

  // Determine which non-main tabs are visible for the currently selected exchange
  const currentExchangeFullData = useMemo(() => {
    if (!selectedExchangeId) return null;
    return selectedExchangesData.find(ex => ex.id === selectedExchangeId) ?? null;
  }, [selectedExchangeId, selectedExchangesData]);

  const visibleTabsForCurrentExchange = useMemo(() => {
    if (!currentExchangeFullData) return [];
    return tabRegistry.filter(tab => {
      if (tab.tabId === "main" || !tab.availableMetrics) return false;
      if (tab.exportOnly) return false;
      if (tab.tabId === "creative") return false; // creative uses its own Customize Columns dropdown
      if (tab.showForExchange && !tab.showForExchange(currentExchangeFullData)) return false;
      if (authUser.user_type === 'Client') {
        if (tab.tabId in tabAccess) {
          if (Array.isArray(tabAccess[tab.tabId]) && tabAccess[tab.tabId].length === 0) return false;
        } else {
          if (!tab.defaultSelectedMetrics) return false;
        }
      }
      return true;
    });
  }, [currentExchangeFullData, authUser.user_type, tabAccess]);

  // Get tab metrics for a specific exchange + tab combination
  const getTabMetrics = (exchangeId: number, tabId: string): string[] => {
    return tabMetricsState[`${exchangeId}:${tabId}`] ?? [];
  };

  const handleTabMetricToggle = (tabId: string, metricId: string) => {
    if (selectedExchangeId == null) return;
    const stateKey = `${selectedExchangeId}:${tabId}`;
    setTabMetricsState(prev => {
      const current = prev[stateKey] ?? [];
      const isSelected = current.includes(metricId);
      return {
        ...prev,
        [stateKey]: isSelected
          ? current.filter(m => m !== metricId)
          : [...current, metricId],
      };
    });
  };

  // Check if any metrics are selected across all exchanges (including keyword metrics)
  useEffect(() => {
    if (exchangeSelectionState.length > 0) {
      const anyExchangeMetrics = exchangeSelectionState.some(exchange =>
        exchange.selectedMetrics && exchange.selectedMetrics.length > 0
      );
      const anyTabMetrics = Object.values(tabMetricsState).some(
        metrics => metrics && metrics.length > 0
      );
      setHasSelectedMetrics(anyExchangeMetrics || anyTabMetrics);
    } else {
      setHasSelectedMetrics(false);
    }
  }, [exchangeSelectionState, tabMetricsState]);


  // Toggle metric selection
  const handleMetricToggle = (exchangeId: number, metric: string) => {
    // Prevent toggling if metric is in empty columns
    const isEmptyMetric = allEmptyColumns.has(formatMetricString(metric));
    if (isEmptyMetric) return;

    // Mark that user has interacted, no longer initial load
    setIsInitialLoad(false);

    setExchangeSelectionState(prev =>
      prev.map(exchange => {
        if (exchange.id === exchangeId) {
          const isSelected = exchange.selectedMetrics.includes(metric);
          return {
            ...exchange,
            selectedMetrics: isSelected
              ? exchange.selectedMetrics.filter(m => m !== metric)
              : [...exchange.selectedMetrics, metric]
          };
        }
        return exchange;
      })
    );
  };

  // Handle AI summary checkbox change
  const handleIncludeAISummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeAISummary(e.target.checked);
  };

  // Close modal handler
  const handleClose = () => {
    closeModal();
  };

  // Clear all selected metrics (including keyword/tab metrics)
  const handleClearAll = () => {
    setAllMetrics(false);
    setExchangeSelectionState(prev =>
      prev.map(exchange => {
        if (exchange.id === selectedExchangeId) {
          return {
            ...exchange,
            selectedMetrics: []
          };
        }
        return exchange;
      })
    );
    if (selectedExchangeId != null) {
      setTabMetricsState(prev => {
        const updated = { ...prev };
        visibleTabsForCurrentExchange.forEach(tab => {
          updated[`${selectedExchangeId}:${tab.tabId}`] = [];
        });
        return updated;
      });
    }
  };

  // Apply default metrics
  const applyDefaultMetrics = () => {
    setAllMetrics(false);
    setExchangeSelectionState(prev =>
      prev.map(exchange => {
        if (exchange.id === selectedExchangeId) {
          return {
            ...exchange,
            selectedMetrics: [...exchange.defaultMetrics]
          };
        }
        return exchange;
      })
    );
  };

  // Select/deselect all metrics (including keyword/tab metrics)
  const applySelectAllMetrics = (checked: boolean) => {
    setAllMetrics(checked);
    setExchangeSelectionState(prev =>
      prev.map(exchange => {
        if (exchange.id === selectedExchangeId) {
          return {
            ...exchange,
            selectedMetrics: checked ? [...exchange.display_metrics] : []
          };
        }
        return exchange;
      })
    );
    if (selectedExchangeId != null) {
      setTabMetricsState(prev => {
        const updated = { ...prev };
        visibleTabsForCurrentExchange.forEach(tab => {
          const stateKey = `${selectedExchangeId}:${tab.tabId}`;
          if (checked) {
            const userPermitted = authUser.user_type === 'Client' && Array.isArray(tabAccess[tab.tabId])
              ? tabAccess[tab.tabId] as string[]
              : null;
            const allIds = userPermitted
              ? tab.availableMetrics!.filter(m => userPermitted.includes(m.id)).map(m => m.id)
              : tab.availableMetrics!.map(m => m.id);
            updated[stateKey] = allIds;
          } else {
            updated[stateKey] = [];
          }
        });
        return updated;
      });
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Get sorted and filtered metrics
  const getSortedMetrics = (exchange: ExchangeMetricsState, emptyColumns: Set<string>) => {
    const { display_metrics, selectedMetrics } = exchange;

    const filteredMetrics = display_metrics.filter(metric => {
      if (!searchTerm.trim()) return true; // Show all if no search term

      const formattedName = formatMetricName(metric).toLowerCase();
      const searchTermLower = searchTerm.toLowerCase();

      // Search only in formatted display name
      return formattedName.includes(searchTermLower);
    });

    // Separate metrics: selected non-empty -> unselected non-empty -> all empty
    const selectedNonEmpty: string[] = [];
    const unselectedNonEmpty: string[] = [];
    const allEmpty: string[] = [];

    // Process selected metrics first, separating empty from non-empty
    selectedMetrics.forEach(selectedMetric => {
      const matchingMetric = filteredMetrics.find(
        metric => formatMetricString(metric) === formatMetricString(selectedMetric)
      );
      if (matchingMetric) {
        const formattedMetric = formatMetricString(matchingMetric);
        const isEmpty = emptyColumns.has(formattedMetric);

        if (isEmpty) {
          allEmpty.push(matchingMetric);
        } else {
          selectedNonEmpty.push(matchingMetric);
        }
      }
    });

    // Add unselected metrics
    const allEmptySet = new Set(allEmpty.map(m => formatMetricString(m)));

    filteredMetrics.forEach(metric => {
      const isSelected = selectedMetrics.some(
        m => formatMetricString(m) === formatMetricString(metric)
      );
      if (!isSelected) {
        const formattedMetric = formatMetricString(metric);
        const isEmpty = emptyColumns.has(formattedMetric);

        if (isEmpty && !allEmptySet.has(formattedMetric)) {
          allEmpty.push(metric);
        } else if (!isEmpty) {
          unselectedNonEmpty.push(metric);
        }
      }
    });

    // Return: selected non-empty -> unselected non-empty -> all empty at bottom
    return [...selectedNonEmpty, ...unselectedNonEmpty, ...allEmpty];
  };

  const currentExchange = exchangeSelectionState.find(ex => ex.id === selectedExchangeId);

  // Get empty columns from Redux for the current exchange
  const reports = useSelector((state: RootState) => state.reports);
  const exchangeDataFromRedux = useMemo(() => {
    if (!currentExchange) return null;
    return selectExchangeByName(reports, currentExchange.name);
  }, [reports, currentExchange]);

  const allEmptyColumns = useMemo(() => {
    const emptyColumns = exchangeDataFromRedux?.emptyColumns || [];
    return new Set<string>(emptyColumns.map((col: string) => formatMetricString(col)));
  }, [exchangeDataFromRedux?.emptyColumns]);

  // Build a map of empty columns per exchange for accurate selected count
  const emptyColumnsByExchange = useMemo(() => {
    const map: Record<number, Set<string>> = {};
    exchangeSelectionState.forEach(exchange => {
      const reduxData = selectExchangeByName(reports, exchange.name);
      const emptyCols = reduxData?.emptyColumns || [];
      map[exchange.id] = new Set(emptyCols.map((col: string) => formatMetricString(col)));
    });
    return map;
  }, [exchangeSelectionState, reports]);

  const formattedDimensions = rawDimensions.map(dimension =>
    formatMetricString(dimension)
  );

  const dimensionSet = new Set(formattedDimensions);

  const dimensionMetrics = currentExchange
    ? getSortedMetrics(currentExchange, allEmptyColumns).filter(metric =>
      dimensionSet.has(formatMetricString(metric))
    )
    : [];

  const remainingMetrics = currentExchange
    ? getSortedMetrics(currentExchange, allEmptyColumns).filter(metric =>
      !dimensionSet.has(formatMetricString(metric))
    )
    : [];


  // Save selected metrics
  const handleSave = () => {
    // Clone the selectedExchangesData
    const updatedExchanges = [...selectedExchangesData];
    let hasChanges = false;

    exchangeSelectionState.forEach(modalExchange => {
      // Find matching exchange in original data (case insensitive)
      const originalExchangeIndex = updatedExchanges.findIndex(
        ex => formatMetricString(ex.name) === formatMetricString(modalExchange.name)
      );

      if (originalExchangeIndex >= 0) {
        const originalExchange = updatedExchanges[originalExchangeIndex];

        // Check if metrics changed (order doesn't matter)
        const metricsChanged =
          modalExchange.selectedMetrics.length !== originalExchange.selectedMetrics.length ||
          !modalExchange.selectedMetrics.every(metric =>
            originalExchange.selectedMetrics.some((saved: string) =>
              formatMetricString(saved) === formatMetricString(metric)
            )
          );

        if (metricsChanged) {
          hasChanges = true;
          //  Update the clone with new metrics
          const updatedExchange = {
            ...originalExchange,
            selectedMetrics: [...modalExchange.selectedMetrics]
          };
          updatedExchanges[originalExchangeIndex] = updatedExchange;
        }
      }
    });

    if (hasChanges) {
      //  Update Redux state with all changes
      updatedExchanges.forEach(exchange => {
        dispatch(setSelectedExchangeMetrics({
          exchangeName: exchange.name,
          metrics: exchange.selectedMetrics
        }));
      });

      //The save operation will update the Redux state,
      //  exchange Table component will fetch data for individual table the UI will reflect these changes
    }

    // Persist tab metrics to Redux if changed
    Object.entries(tabMetricsState).forEach(([stateKey, metrics]) => {
      const [exchangeIdStr, tabId] = stateKey.split(':');
      const cacheKey = buildCacheKey(Number(exchangeIdStr), tabId);
      const existing = tabDataEntries[cacheKey]?.selectedMetrics ?? [];
      const changed = metrics.length !== existing.length ||
        !metrics.every((m, i) => m === existing[i]);
      if (changed) {
        dispatch(setTabSelectedMetrics({ cacheKey, metrics }));
      }
    });

    closeModal();
  };

  // Get current metrics data in the user's custom exchange order, excluding hidden exchanges
  const getCurrentMetricsData = () => {
    const visibleState = visibleExchangeIds.length > 0
      ? exchangeSelectionState.filter(ex => visibleExchangeIds.includes(ex.id))
      : exchangeSelectionState;

    return visibleState.map(modalExchange => {
      const original = selectedExchangesData.find(
        ex => formatMetricString(ex.name) === formatMetricString(modalExchange.name)
      );
      if (!original) return null;
      return {
        ...original,
        selectedMetrics: [...modalExchange.selectedMetrics]
      };
    }).filter(Boolean);
  };

  const tabContainerRef = useRef<HTMLDivElement>(null);

  // Scroll tabs horizontally
  const scrollTabsByOne = (direction: "left" | "right") => {
    const container = tabContainerRef.current;
    if (!container) return;

    const tab = container.querySelector(".tab");
    if (!tab) return;

    const tabWidth = (tab as HTMLElement).offsetWidth;
    const scrollAmount = direction === "right" ? tabWidth : -tabWidth;

    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const scrollLeft = () => scrollTabsByOne("left");
  const scrollRight = () => scrollTabsByOne("right");


  // Handle export button click
  const handleExportClick = async (aiSummary: any) => {
    setLoading(true);
    const currentMetrics = getCurrentMetricsData();

    // Check if all exchanges have empty selectedLineItems
    const allLineItemsEmpty = currentMetrics.every(exchange =>
      !exchange.selectedLineItems || exchange.selectedLineItems.length === 0
    );

    if (allLineItemsEmpty) {
      toast.error("Select line items to export");
      setLoading(false);
      return;
    }

    // Build tab_metrics for the export payload using ALL permitted metrics (not user-selected).
    // Export should include every metric the user is allowed to see.
    const tabMetricsPayload: Record<string, string[]> = {};
    tabRegistry.forEach(tab => {
      if (tab.tabId === "main" || !tab.availableMetrics) return;
      // Check if user has access to this tab
      if (authUser.user_type === 'Client') {
        if (tab.tabId in tabAccess) {
          if (Array.isArray(tabAccess[tab.tabId]) && tabAccess[tab.tabId].length === 0) return;
        } else {
          if (!tab.defaultSelectedMetrics) return;
        }
      }
      const userPermitted = authUser.user_type === 'Client' && Array.isArray(tabAccess[tab.tabId])
        ? tabAccess[tab.tabId] as string[]
        : null;
      const allPermittedIds = userPermitted
        ? tab.availableMetrics.filter(m => userPermitted.includes(m.id)).map(m => m.id)
        : tab.availableMetrics.map(m => m.id);
      if (allPermittedIds.length > 0) {
        tabMetricsPayload[tab.tabId] = allPermittedIds;
      }
    });

    try {
      const response = await axiosInstance.post("/excel-export",
        {
          start_date: filters.startDate,
          end_date: filters.endDate,
          orderId: filters.selectedOrder,
          exchangeData: currentMetrics,
          data_by: reportBy,
          allExchange: true,
          aiSummary: aiSummary,
          tab_metrics: Object.keys(tabMetricsPayload).length > 0 ? tabMetricsPayload : null,
        },
        {
          responseType: 'blob',
        }
      );
      const contentType = response.headers["content-type"];
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const jsonData = JSON.parse(text);
        if (!jsonData.flag) {
          toast.error("No data available for export");
          setLoading(false);
          handleSave();
          return;
        }
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", orderData?.name + ".xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Report exported successfully");
      setLoading(false);
      handleSave();
    } catch (error) {
      toast.error('Something went wrong!');
      setLoading(false);
      handleSave();
    }
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={handleClose}
      className="custom-modal"
      contentLabel="Customize Metrics Modal"
    >
      <div className="modal-header">
        <h5>Customize Metrics</h5>
        <button onClick={handleClose} className="btn-close" aria-label="Close Modal">
        </button>
      </div>

      <div className="modal-body">
        <div className="tab-scroll-wrapper">
          {showScrollButtons && (
            <span
              className={`scroll-btn ${!canScrollLeft ? "disabled" : ""}`}
              onClick={canScrollLeft ? scrollLeft : undefined}
            >
              &laquo;
            </span>
          )}
          <div className="tab-scroll-container" ref={tabContainerRef}>
            <DndContext
              sensors={tabSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleTabDragEnd}
            >
              <SortableContext
                items={exchangeSelectionState.map(ex => ex.id)}
                strategy={horizontalListSortingStrategy}
              >
                {exchangeSelectionState.map((exchange) => (
                  <SortableTab
                    key={exchange.id}
                    exchange={exchange}
                    isActive={selectedExchangeId === exchange.id}
                    selectedCount={
                      (exchange.selectedMetrics?.filter(m => !emptyColumnsByExchange[exchange.id]?.has(formatMetricString(m))).length || 0) +
                      Object.entries(tabMetricsState)
                        .filter(([key]) => key.startsWith(`${exchange.id}:`))
                        .reduce((sum, [, metrics]) => sum + metrics.length, 0)
                    }
                    onClick={() => setSelectedExchangeId(exchange.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
          {showScrollButtons && (
            <span
              className={`scroll-btn ${!canScrollRight ? "disabled" : ""}`}
              onClick={canScrollRight ? scrollRight : undefined}
            >
              &raquo;
            </span>
          )}
        </div>
        {currentExchange && (
          <>
            <div className="platform-name-header">
              <div className="platform-name">
                {currentExchange?.iconUrl && (
                  <div className="custom-icon-box">
                    <img
                      src={currentExchange.iconUrl}
                      alt={`${currentExchange.name} icon`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                      width="auto"
                      height="17px"
                    />
                  </div>
                )}
                <h3>{currentExchange.name}</h3>
              </div>
              <div className="header-right-div">
                <input
                  type="text"
                  placeholder="Search "
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="column-search-input form-control"
                />
                <div className="header-btn-grp">
                  <button type="button" className="btn btn-outline-secondary button-secondary"
                    onClick={applyDefaultMetrics}
                    disabled={currentExchange?.defaultMetrics?.length == 0} >
                    Default
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary button-secondary inline-nowrap"
                    onClick={() => applySelectAllMetrics(true)}
                  >
                    Select All
                  </button>
                  <button
                    className="btn btn-outline-secondary button-secondary inline-nowrap"
                    onClick={handleClearAll}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
            <div className="metrics-container">
              <div className="metrics-column-scroll">
                <h6>Dimensions</h6>
                {dimensionMetrics.length > 0 ?
                  <div className="metrics-section">
                    {dimensionMetrics.map((metric, index) => {
                      const isSelected = currentExchange.selectedMetrics.some(
                        m => formatMetricString(m) === formatMetricString(metric)
                      );
                      const isEmptyMetric = allEmptyColumns.has(formatMetricString(metric));
                      return (
                        <div
                          className={`metric-tile custom-tile ${isSelected ? 'selected' : ''} ${isEmptyMetric ? 'all-empty-metric' : ''}`}
                          key={index}
                          onClick={(e) => {
                            // Only handle clicks directly on the tile background
                            if (e.target === e.currentTarget && !isEmptyMetric) {
                              handleMetricToggle(currentExchange.id, metric);
                            }
                          }}
                          style={{ cursor: isEmptyMetric ? 'not-allowed' : 'pointer', opacity: isEmptyMetric ? 0.6 : 1 }}
                          title={isEmptyMetric ? "No data available for current selection" : ""}
                        >
                          <div className="metric-control-wrapper">
                            <input
                              id={`metric-checkbox-dimension-${index}`}
                              className="form-check-input checkbox-input"
                              type="checkbox"
                              checked={isSelected && !isEmptyMetric}
                              onChange={() => handleMetricToggle(currentExchange.id, metric)}
                              onClick={(e) => e.stopPropagation()}
                              disabled={isEmptyMetric}
                            />
                            <label
                              htmlFor={`metric-checkbox-dimension-${index}`}
                              className="checkbox-label custom-label"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isEmptyMetric) {
                                  handleMetricToggle(currentExchange.id, metric);
                                }
                              }}
                            >
                              <span className="metric-name">
                                {formatMetricName(metric)}
                              </span>
                            </label>
                            {isEmptyMetric ? (
                              <span className="empty-indicator">N/A</span>
                            ) : (
                              currentExchange.defaultMetrics.includes(metric) && (
                                <span className="default-metric-label">Default</span>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div> :
                  <p className="inline-margin-top-20">No Dimensions available</p>
                }
                {/* Tab Metrics sections — shown below Dimensions for each visible non-main tab */}
                {visibleTabsForCurrentExchange.map(tab => {
                  const allMetricsForTab = tab.availableMetrics!;
                  const userPermitted = authUser.user_type === 'Client' && Array.isArray(tabAccess[tab.tabId])
                    ? tabAccess[tab.tabId] as string[]
                    : null;
                  const availableMetrics = userPermitted
                    ? allMetricsForTab.filter(m => userPermitted.includes(m.id))
                    : allMetricsForTab;
                  const selected = selectedExchangeId != null
                    ? getTabMetrics(selectedExchangeId, tab.tabId)
                    : [];
                  const filteredTabMetrics = searchTerm.trim()
                    ? availableMetrics.filter(m => m.label.toLowerCase().includes(searchTerm.toLowerCase()))
                    : availableMetrics;

                  if (filteredTabMetrics.length === 0) return null;

                  return (
                    <div key={tab.tabId} className="keyword-metrics-section">
                      <h6>{tab.tabLabel} Metrics</h6>
                      <div className="metrics-section">
                        {filteredTabMetrics.map((metric, index) => {
                          const isSelected = selected.includes(metric.id);
                          return (
                            <div
                              className={`metric-tile custom-tile ${isSelected ? 'selected' : ''}`}
                              key={`${tab.tabId}-${metric.id}`}
                              onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                  handleTabMetricToggle(tab.tabId, metric.id);
                                }
                              }}
                            >
                              <div className="metric-control-wrapper">
                                <input
                                  id={`metric-checkbox-kw-${tab.tabId}-${index}`}
                                  className="form-check-input checkbox-input"
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTabMetricToggle(tab.tabId, metric.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <label
                                  htmlFor={`metric-checkbox-kw-${tab.tabId}-${index}`}
                                  className="checkbox-label custom-label"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleTabMetricToggle(tab.tabId, metric.id);
                                  }}
                                >
                                  <span className="metric-name">{metric.label}</span>
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="metrics-column-scroll">
                <h6>Metrics</h6>
                {remainingMetrics.length > 0 ?
                <div className="metrics-section">
                  {remainingMetrics.map((metric, index) => {
                    const isSelected = currentExchange.selectedMetrics.some(
                      m => formatMetricString(m) === formatMetricString(metric)
                    );
                    const isEmptyMetric = allEmptyColumns.has(formatMetricString(metric));
                    return (
                      <div
                        className={`metric-tile custom-tile ${isSelected ? 'selected' : ''} ${isEmptyMetric ? 'all-empty-metric' : ''}`}
                        key={index}
                        onClick={(e) => {
                          // Only handle clicks directly on the tile background
                          if (e.target === e.currentTarget && !isEmptyMetric) {
                            handleMetricToggle(currentExchange.id, metric);
                          }
                        }}
                        style={{ cursor: isEmptyMetric ? 'not-allowed' : 'pointer', opacity: isEmptyMetric ? 0.6 : 1 }}
                        title={isEmptyMetric ? "No data available for current selection" : ""}
                      >
                        <div className="metric-control-wrapper">
                          <input
                            id={`metric-checkbox-remaining-${index}`}
                            className="form-check-input checkbox-input"
                            type="checkbox"
                            checked={isSelected && !isEmptyMetric}
                            onChange={() => handleMetricToggle(currentExchange.id, metric)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isEmptyMetric}
                          />
                          <label
                            htmlFor={`metric-checkbox-remaining-${index}`}
                            className="checkbox-label custom-label"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!isEmptyMetric) {
                                handleMetricToggle(currentExchange.id, metric);
                              }
                            }}
                          >
                            <span className="metric-name">
                              {formatMetricName(metric)}
                            </span>
                          </label>
                          {isEmptyMetric ? (
                            <span className="empty-indicator">N/A</span>
                          ) : (
                            currentExchange.defaultMetrics.includes(metric) && (
                              <span className="default-metric-label">Default</span>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                :<p className="inline-margin-top-20">No Metrics available</p>}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="footer-grp">
        <label className="checkbox-label custom-label ">
          <input
            className="form-check-input checkbox-input"
            type="checkbox"
            checked={includeAISummary}
            onChange={handleIncludeAISummaryChange}
          />
          Include AI Summary
        </label>
        <div className="footer-btn-grp">
          <button
            className="btn btn-primary button-primary"
            type="button"
            onClick={() => handleExportClick(includeAISummary)}
            disabled={loading || !hasSelectedMetrics || allExchangesHaveNoData}
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm"></span>
            ) : (
              "Export All"
            )}
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary button-primary"
            disabled={loading}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomizeMetricsModal;
